import React, { useCallback, useEffect, useState } from 'react';
import { MarkDown, mixins } from '@sy/component-library';
import type { MarkDownComponents } from '@sy/component-library';
import type { CacheLandingPage, CacheLandingPageContent } from '@sy/ssr-types';

import Avatar from '~/components/Avatar';
import FullscreenVideoModal from '~/components/FullscreenVideoModal';

import {
	CTARow,
	ContainerImage640x560,
	FaqBlock,
	FaqBlockImage,
	FaqBlockLi,
	FaqBlockLink,
	FaqBlockOl,
	FaqBlockText,
	FaqBlockUl,
	Header,
	HeroImage,
	Highlight,
	HighlightProps,
	ImageBlock,
	ImageBlockProps,
	ImageBlockBgColors,
	Signup,
	Video640x560,
	Testimonial,
	Usp,
} from '~/components/Marketing';

import type {
	HeaderProps,
	HeroImageProps,
	CTARowProps,
} from '~/components/Marketing';
import ResponsiveText from '~/components/ResponsiveText';
import LinkBody from '~/components/LinkBodyV2';

import analytics from '~/services/analytics';
import WatchButton from '~/components/Marketing/components/WatchButton';
import MobileLandingSignup from '~/scenes/Signup/scenes/MobileLandingSignup';
import { TestimonialWrapper } from './LandingPageTemplate.styles';

const { mediaQueries } = mixins;

type LandingPageTemplateProps = {
	data: CacheLandingPage;
	NextImage: React.ElementType;
};

const MarketingLink = ({
	children,
	...rest
}: React.ComponentProps<typeof LinkBody>) => (
	<LinkBody
		{...rest}
		newTab={rest.href.startsWith('http://') || rest.href.startsWith('https://')}
	>
		{children}
	</LinkBody>
);

const getMarkdownComponents = (color?: string) =>
	({
		a: props => <MarketingLink {...props} color={color} />,
		p: props => <ResponsiveText {...props} color={color} variant="body" />,
	} as MarkDownComponents);

const contentMapper = ({
	content,
	index,
	NextImage,
	openVideoModal,
	redesign,
}: {
	content: CacheLandingPageContent;
	index: number;
	NextImage: React.ElementType;
	openVideoModal: Function;
	redesign: boolean;
}) => {
	switch (content.type) {
		case 'landingPageHeader': {
			const props: HeaderProps & { redesign: boolean } = {
				title: content.title,
				redesign,
			};
			if (content.showAuthForm) {
				props.cta = <Signup />;
			}
			if (content.subtitle) {
				props.markdownSubtitle = (
					<MarkDown
						key={`${content.type}-${index}-0-subtitle`}
						components={getMarkdownComponents()}
						content={content.subtitle}
					/>
				);
			}
			return <Header key={`${content.type}-${index}`} {...props} />;
		}

		case 'landingPageHighlight': {
			const props: HighlightProps = {
				title: content.title,
				body: content.subtitle,
				cta: {
					url: content.linkUrl,
					title: content.linkTitle,
				},
			};

			return <Highlight key={`${content.type}-${index}`} {...props} />;
		}

		case 'landingPageTextAndMedia': {
			const props: ImageBlockProps = {
				header: content.title,
				subtitle: content.subtitle && (
					<MarkDown
						components={getMarkdownComponents()}
						content={content.subtitle}
					/>
				),
				color: content.backgroundColor as ImageBlockBgColors,
				reverse: content.textFirst,
			};

			if (content.media) {
				if (content.media.type === 'responsiveImage') {
					const aspectRatio =
						(content.media?.image?.dimensions?.width || 1) /
						(content.media?.image?.dimensions?.height || 1);
					props.image = (
						<ContainerImage640x560 aspectRatio={aspectRatio}>
							<NextImage
								alt={content.media.image?.description || ''}
								src={content.media.image?.url}
								layout="fill"
								sizes={`${mediaQueries.belowLarge} 100vw, 50vw`}
							/>
						</ContainerImage640x560>
					);
					// TODO: support smallImage
				}

				if (content.media.type === 'responsiveVideo') {
					props.image = (
						<Video640x560
							autoPlay
							loop
							muted
							playsInline
							src={content.media.video?.url}
							poster={content.media.poster}
						/>
					);
					// TODO: support smallVideo
				}
			}

			if (content.quote) {
				props.name = content.quote.name;
				props.role = content.quote.jobTitle;
				props.quote = content.quote.quote;
				props.avatar = (
					<Avatar src={content.quote.image?.url} NextImage={NextImage} />
				);
			}

			return <ImageBlock key={`${content.type}-${index}`} {...props} />;
		}

		case 'responsiveImage': {
			const props: HeroImageProps = {
				NextImage,
			};

			if (content.image) {
				props.largeImage = {
					src: content.image.url,
					height: content.image.dimensions?.height,
					width: content.image.dimensions?.width,
					aspectRatio:
						(content.image.dimensions?.width || 1) /
						(content.image.dimensions?.height || 1),
				};
			}

			if (content.smallImage) {
				props.smallImage = {
					src: content.smallImage.url,
					height: content.smallImage.dimensions?.height,
					width: content.smallImage.dimensions?.width,
					aspectRatio:
						(content.smallImage.dimensions?.width || 1) /
						(content.smallImage.dimensions?.height || 1),
				};
			}

			return (
				<HeroImage
					key={`${content.type}-${index}`}
					alt={content.image?.description}
					{...props}
				/>
			);
		}

		case 'faqs': {
			const components = {
				a: FaqBlockLink,
				img: ({ src, alt }) => (
					<FaqBlockImage src={src} alt={alt} NextImage={NextImage} />
				),
				li: FaqBlockLi,
				ol: FaqBlockOl,
				p: FaqBlockText,
				ul: FaqBlockUl,
			} as MarkDownComponents;

			const props = {
				header: content.title,
				faqList: content.faqs
					? content.faqs.map(faq => ({
							question: faq.question,
							answer: faq.answer ? (
								<MarkDown components={components} content={faq.answer} />
							) : null,
					  }))
					: [],
			};

			return <FaqBlock key={`${content.type}-${index}`} {...props} />;
		}

		case 'landingPageCtaRow': {
			const props: CTARowProps = {
				title: content.title,
				subtitle2: content.subtitle && (
					<MarkDown
						components={getMarkdownComponents('white')}
						content={content.subtitle}
					/>
				),
				body2: content.body && (
					<MarkDown
						components={getMarkdownComponents('white')}
						content={content.body}
					/>
				),
			};

			if (content.showAuthForm) {
				props.cta = <Signup />;
			}

			return <CTARow key={`${content.type}-${index}`} {...props} />;
		}

		case 'quote': {
			const props = {
				altText: content.name,
				quote: content.quote,
				name: content.name,
				role: content.jobTitle,
				imgWebpSrc: content.image?.url,
				imgFallbackSrc: content.image?.url,
				...(content.embedYoutubeVideoId && content.embedLinkText
					? {
							watchButton: (
								<WatchButton
									label={content.embedLinkText}
									onClick={() => openVideoModal(content.embedYoutubeVideoId)}
								/>
							),
					  }
					: {}),
			};

			return (
				<TestimonialWrapper key={`${content.type}-${index}`}>
					<Testimonial NextImage={NextImage} {...props} />
				</TestimonialWrapper>
			);
		}

		case 'usp': {
			const props = {
				title: content.title,
				backgroundColor: content.backgroundColor,
				cards: content.cards,
			};

			return (
				<Usp
					key={`${content.type}-${index}`}
					NextImage={NextImage}
					{...props}
				/>
			);
		}

		case 'mobileSignupForm': {
			return <MobileLandingSignup gutterBottom />;
		}

		default:
			return null;
	}
};

const mapper = ({
	content,
	NextImage,
	openVideoModal,
	redesign,
}: {
	content?: CacheLandingPageContent[];
	NextImage: React.ElementType;
	openVideoModal: Function;
	redesign: boolean;
}) =>
	content?.map((eachContent, index) =>
		contentMapper({
			content: eachContent,
			index,
			NextImage,
			openVideoModal,
			redesign,
		}),
	);

const LandingPageTemplate = ({ data, NextImage }: LandingPageTemplateProps) => {
	const [videoId, setVideoId] = useState<string | undefined>(undefined);
	const openVideoModal = useCallback((id: string) => setVideoId(id), []);
	const closeVideoModal = useCallback(() => setVideoId(undefined), []);

	useEffect(() => {
		analytics.page(analytics.events.category.MARKETING, data.title);
	}, [data.title]);

	const redesign =
		data.content?.some(content => content.type === 'mobileSignupForm') ?? false;

	return (
		<>
			{mapper({
				content: data.content,
				NextImage,
				openVideoModal,
				redesign,
			})}

			<FullscreenVideoModal
				isOpen={!!videoId}
				onRequestClose={closeVideoModal}
				youtubeVideoId={videoId}
			/>
		</>
	);
};

export default LandingPageTemplate;
